import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STORAGE_KEY = 'nyumba_ai_messages_v3';
const ID_KEY = 'nyumba_ai_conversation_id_v3';
const SAVED_SEARCHES_KEY = 'nyumba_ai_saved_searches_v1';
const LIVE_SESSION_KEY = 'nyumba_live_session_v1';

const discoveryActions = [
  { label: 'Homes under UGX 1B', message: 'Show me 3 homes in Kampala under UGX 1B.' },
  { label: 'Best for diaspora', message: 'I am in the diaspora. What projects are best for remote investment?' },
  { label: 'Compare property types', message: 'Compare apartments and villas for long-term investment.' },
  { label: 'Book viewing', message: 'I want to schedule a viewing this week.' },
  { label: 'Payment options', message: 'What payment plans are available?' },
];

const defaultWelcome = [
  {
    role: 'assistant',
    content:
      'Welcome to Nyumba AI. I can help you discover homes by budget, location, and timeline, then guide you to the right next step.',
  },
];

const wizardDefaults = {
  goal: '',
  location: '',
  budget: '',
  propertyType: '',
  timeline: '',
};

const escapeHtml = (text) =>
  text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');

const formatInline = (text) => text.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');

const renderMarkdownBasic = (text) => {
  const escaped = escapeHtml(text || '');
  const lines = escaped.split(/\r?\n/);
  let html = '';
  let inList = false;
  for (const line of lines) {
    const listMatch = line.match(/^\s*[-*]\s+(.*)$/);
    if (listMatch) {
      if (!inList) {
        html += '<ul class="list-disc pl-5 space-y-1">';
        inList = true;
      }
      html += `<li>${formatInline(listMatch[1])}</li>`;
      continue;
    }
    if (inList) {
      html += '</ul>';
      inList = false;
    }
    if (!line.trim()) {
      html += '<div class="h-2"></div>';
      continue;
    }
    html += `<p>${formatInline(line)}</p>`;
  }
  if (inList) {
    html += '</ul>';
  }
  return { __html: html };
};

const getRouteName = (pathname) => {
  if (pathname === '/') return 'Home';
  if (pathname.startsWith('/about')) return 'About';
  if (pathname.startsWith('/services')) return 'Services';
  if (pathname.startsWith('/projects')) return 'Projects';
  if (pathname.startsWith('/investors')) return 'Investors';
  if (pathname.startsWith('/virtual-experience')) return 'Virtual Experience';
  if (pathname.startsWith('/consultation')) return 'Consultation';
  if (pathname.startsWith('/blog')) return 'Blog';
  if (pathname.startsWith('/contact')) return 'Contact';
  return 'Page';
};

const getUtmParams = () => {
  const params = new URLSearchParams(window.location.search || '');
  const utm = {};
  ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'].forEach((key) => {
    const value = params.get(key);
    if (value) utm[key] = value;
  });
  return Object.keys(utm).length ? utm : undefined;
};

const getConversationId = () => {
  const existing = localStorage.getItem(ID_KEY);
  if (existing) return existing;
  const newId = window.crypto?.randomUUID?.() || `conv_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
  localStorage.setItem(ID_KEY, newId);
  return newId;
};

const buildWizardPrompt = (wizard) => {
  const chunks = [
    'Help me find the best Abkasan property options with this profile:',
    `- Goal: ${wizard.goal || 'not specified'}`,
    `- Location preference: ${wizard.location || 'not specified'}`,
    `- Budget: ${wizard.budget || 'not specified'}`,
    `- Property type: ${wizard.propertyType || 'not specified'}`,
    `- Timeline: ${wizard.timeline || 'not specified'}`,
    'Give me top matches, why each fits, and recommended next steps.',
  ];
  return chunks.join('\n');
};

const makeSavedSearchLabel = (wizard) => {
  const parts = [wizard.propertyType, wizard.location, wizard.budget].filter(Boolean);
  if (!parts.length) return 'Saved search';
  return parts.join(' | ');
};

const FloatingAbkasanAI = () => {
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('ai');
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [messages, setMessages] = useState(defaultWelcome);
  const [featuredProjects, setFeaturedProjects] = useState([]);
  const [savedSearches, setSavedSearches] = useState([]);
  const [wizard, setWizard] = useState(wizardDefaults);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [compareSelection, setCompareSelection] = useState([]);

  const [liveSession, setLiveSession] = useState(null);
  const [liveMessages, setLiveMessages] = useState([]);
  const [liveLoading, setLiveLoading] = useState(false);
  const [liveInput, setLiveInput] = useState('');
  const [liveForm, setLiveForm] = useState({ name: '', email: '', phone: '', initial_message: '' });

  useEffect(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        if (Array.isArray(parsed) && parsed.length) setMessages(parsed);
      } catch {
        setMessages(defaultWelcome);
      }
    }

    const storedSearches = localStorage.getItem(SAVED_SEARCHES_KEY);
    if (storedSearches) {
      try {
        const parsed = JSON.parse(storedSearches);
        if (Array.isArray(parsed)) setSavedSearches(parsed.slice(0, 8));
      } catch {
        setSavedSearches([]);
      }
    }

    const storedLive = localStorage.getItem(LIVE_SESSION_KEY);
    if (storedLive) {
      try {
        const parsed = JSON.parse(storedLive);
        if (parsed?.session_id && parsed?.guest_token) setLiveSession(parsed);
      } catch {
        setLiveSession(null);
      }
    }
  }, []);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(messages.slice(-50)));
  }, [messages]);

  useEffect(() => {
    localStorage.setItem(SAVED_SEARCHES_KEY, JSON.stringify(savedSearches.slice(0, 8)));
  }, [savedSearches]);

  useEffect(() => {
    if (liveSession?.session_id && liveSession?.guest_token) {
      localStorage.setItem(LIVE_SESSION_KEY, JSON.stringify(liveSession));
    } else {
      localStorage.removeItem(LIVE_SESSION_KEY);
    }
  }, [liveSession]);

  useEffect(() => {
    let cancelled = false;
    const loadFeatured = async () => {
      try {
        const response = await axios.get(`${API}/projects?featured=true&limit=6`);
        if (!cancelled && Array.isArray(response.data)) setFeaturedProjects(response.data.slice(0, 6));
      } catch {
        if (!cancelled) setFeaturedProjects([]);
      }
    };
    loadFeatured();
    return () => {
      cancelled = true;
    };
  }, []);

  const history = useMemo(
    () =>
      messages
        .filter((msg) => msg.role === 'user' || msg.role === 'assistant')
        .slice(-10)
        .map((msg) => ({ role: msg.role, content: msg.content })),
    [messages]
  );

  const sendMessage = async (text) => {
    const trimmed = text.trim();
    if (!trimmed || loading) return;
    setMessages((prev) => [...prev, { role: 'user', content: trimmed }]);
    setInput('');
    setLoading(true);
    try {
      const payload = {
        message: trimmed,
        pageUrl: window.location.href,
        context: { source: 'public_site', page: getRouteName(location.pathname), utm: getUtmParams() },
        conversationId: getConversationId(),
        history,
      };
      const response = await axios.post(`${API}/ai/chat`, payload);
      const reply = response.data?.reply || 'Thanks. Tell me your budget and location and I will narrow options for you.';
      setMessages((prev) => [...prev, { role: 'assistant', content: reply }]);
    } catch {
      setMessages((prev) => [...prev, { role: 'assistant', content: 'I am having trouble right now. Please try again shortly.' }]);
    } finally {
      setLoading(false);
    }
  };

  const pollLiveSession = useCallback(async () => {
    if (!liveSession?.session_id || !liveSession?.guest_token) return;
    try {
      const response = await axios.get(`${API}/live-chat/sessions/${liveSession.session_id}`, {
        headers: { 'X-Guest-Token': liveSession.guest_token },
      });
      setLiveSession((prev) => ({
        ...(prev || {}),
        session_id: liveSession.session_id,
        guest_token: liveSession.guest_token,
        status: response.data?.session?.status || prev?.status,
        assigned_admin_id: response.data?.session?.assigned_admin_id || null,
      }));
      if (Array.isArray(response.data?.messages)) {
        setLiveMessages(response.data.messages);
      }
    } catch (error) {
      // keep existing state; session may be temporarily unavailable
    }
  }, [liveSession]);

  useEffect(() => {
    if (activeTab !== 'live' || !open || !liveSession?.session_id) return;
    pollLiveSession();
    const interval = setInterval(pollLiveSession, 4000);
    return () => clearInterval(interval);
  }, [activeTab, open, liveSession?.session_id, pollLiveSession]);

  const startLiveChat = async () => {
    if (liveLoading) return;
    if (!liveForm.name.trim() || !liveForm.email.trim() || !liveForm.initial_message.trim()) return;
    setLiveLoading(true);
    try {
      const response = await axios.post(`${API}/live-chat/sessions`, liveForm);
      setLiveSession({
        session_id: response.data.session_id,
        guest_token: response.data.guest_token,
        status: response.data.status,
        assigned_admin_id: response.data.assigned_admin_id || null,
      });
      setLiveMessages([]);
      setLiveInput('');
      await pollLiveSession();
    } catch (error) {
      setLiveMessages((prev) => [...prev, { sender_type: 'system', content: 'Could not start live chat right now.' }]);
    } finally {
      setLiveLoading(false);
    }
  };

  const sendLiveMessage = async () => {
    const content = liveInput.trim();
    if (!content || liveLoading || !liveSession?.session_id) return;
    setLiveLoading(true);
    try {
      await axios.post(
        `${API}/live-chat/sessions/${liveSession.session_id}/messages`,
        { content },
        { headers: { 'X-Guest-Token': liveSession.guest_token } }
      );
      setLiveInput('');
      await pollLiveSession();
    } catch (error) {
      setLiveMessages((prev) => [...prev, { sender_type: 'system', content: 'Message failed to send. Please retry.' }]);
    } finally {
      setLiveLoading(false);
    }
  };

  const askAboutProject = (project) => {
    const locationText = project.location ? ` in ${project.location}` : '';
    sendMessage(`Tell me about ${project.title}${locationText}. Include price guidance, timeline, and why it is a good fit.`);
  };

  const toggleCompare = (projectId) => {
    setCompareSelection((prev) => {
      if (prev.includes(projectId)) return prev.filter((id) => id !== projectId);
      if (prev.length >= 3) return prev;
      return [...prev, projectId];
    });
  };

  const compareSelected = () => {
    if (compareSelection.length < 2) return;
    const selected = featuredProjects.filter((project) => compareSelection.includes(project.id));
    const names = selected.map((project) => project.title).join(' vs ');
    sendMessage(`Compare these projects: ${names}. Include who each option fits best, budget considerations, and expected timelines.`);
  };

  const runGuidedSearch = () => sendMessage(buildWizardPrompt(wizard));
  const saveGuidedSearch = () => {
    const entry = { id: window.crypto?.randomUUID?.() || `search_${Date.now()}`, label: makeSavedSearchLabel(wizard), wizard };
    setSavedSearches((prev) => [entry, ...prev.filter((item) => item.label !== entry.label)].slice(0, 8));
  };
  const loadSavedSearch = (entry) => {
    setWizard(entry.wizard || wizardDefaults);
    setWizardOpen(true);
    sendMessage(buildWizardPrompt(entry.wizard || wizardDefaults));
  };
  const deleteSavedSearch = (entryId) => setSavedSearches((prev) => prev.filter((item) => item.id !== entryId));

  return (
    <>
      {open && <button type="button" className="fixed inset-0 bg-black/70 backdrop-blur-sm z-40" aria-label="Close Nyumba AI" onClick={() => setOpen(false)} />}
      <div className="fixed bottom-4 right-4 sm:bottom-6 sm:right-6 z-50">
        <button
          type="button"
          className="h-12 px-5 rounded-full bg-gradient-to-r from-[#F0D8A8] via-[#EAC27C] to-[#DFA45A] text-[#1A1208] shadow-[0_12px_30px_rgba(223,164,90,0.35)] hover:shadow-[0_16px_36px_rgba(223,164,90,0.45)] transition-shadow border border-[#F6E1B8]/70 font-semibold"
          onClick={() => setOpen(true)}
        >
          AI Assistant
        </button>
      </div>

      <div className={`fixed z-50 left-3 right-3 top-16 bottom-20 sm:left-auto sm:right-6 sm:top-20 sm:bottom-24 sm:w-[min(92vw,780px)] transition-all duration-300 ${open ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6 pointer-events-none'}`}>
        <div className="h-full rounded-3xl border border-[#F6E1B8]/30 bg-gradient-to-b from-[#0E0B08] via-[#0C0A08] to-[#0A0908] shadow-[0_24px_80px_rgba(0,0,0,0.75)] backdrop-blur-md ring-1 ring-[#F6E1B8]/10 flex flex-col">
          <div className="flex items-center justify-between px-5 py-4 border-b border-white/10">
            <div>
              <p className="text-xs uppercase tracking-wide text-[#F6E1B8]/70">Abkasan Luxe Homes</p>
              <h3 className="text-xl font-display text-white">Abkasan AI</h3>
              <p className="text-xs text-[#BFAF95]">AI + Live Admin chat</p>
            </div>
            <button type="button" className="text-[#CFC6B8] hover:text-white" onClick={() => setOpen(false)}>x</button>
          </div>

          <div className="px-5 pt-4 shrink-0">
            <div className="inline-flex rounded-full border border-white/10 bg-white/[0.03] p-1">
              <button type="button" onClick={() => setActiveTab('ai')} className={`px-3 py-1 text-xs rounded-full ${activeTab === 'ai' ? 'bg-[#E9C98F]/20 text-[#F0D8A8]' : 'text-[#CFC6B8]'}`}>AI Assistant</button>
              <button type="button" onClick={() => setActiveTab('live')} className={`px-3 py-1 text-xs rounded-full ${activeTab === 'live' ? 'bg-[#E9C98F]/20 text-[#F0D8A8]' : 'text-[#CFC6B8]'}`}>Live Admin</button>
            </div>
          </div>

          {activeTab === 'ai' ? (
            <div className="px-5 py-4 space-y-4 flex-1 min-h-0 overflow-y-auto">
              <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-3">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-xs text-[#CFC6B8]">Guided search</p>
                  <button type="button" onClick={() => setWizardOpen((prev) => !prev)} className="text-xs px-2 py-1 rounded-full border border-white/10 text-[#CFC6B8] hover:text-white hover:bg-white/10">
                    {wizardOpen ? 'Collapse' : 'Open'}
                  </button>
                </div>
                {wizardOpen && (
                  <div className="space-y-2">
                    <div className="grid grid-cols-2 gap-2">
                      <input className="input-luxury bg-white/5 border border-white/10 text-[#F0E9DD] placeholder:text-[#B9B0A2] text-xs rounded-xl" placeholder="Goal: live / invest" value={wizard.goal} onChange={(e) => setWizard((prev) => ({ ...prev, goal: e.target.value }))} />
                      <input className="input-luxury bg-white/5 border border-white/10 text-[#F0E9DD] placeholder:text-[#B9B0A2] text-xs rounded-xl" placeholder="Location" value={wizard.location} onChange={(e) => setWizard((prev) => ({ ...prev, location: e.target.value }))} />
                      <input className="input-luxury bg-white/5 border border-white/10 text-[#F0E9DD] placeholder:text-[#B9B0A2] text-xs rounded-xl" placeholder="Budget" value={wizard.budget} onChange={(e) => setWizard((prev) => ({ ...prev, budget: e.target.value }))} />
                      <input className="input-luxury bg-white/5 border border-white/10 text-[#F0E9DD] placeholder:text-[#B9B0A2] text-xs rounded-xl" placeholder="Property type" value={wizard.propertyType} onChange={(e) => setWizard((prev) => ({ ...prev, propertyType: e.target.value }))} />
                    </div>
                    <input className="input-luxury bg-white/5 border border-white/10 text-[#F0E9DD] placeholder:text-[#B9B0A2] text-xs rounded-xl w-full" placeholder="Timeline" value={wizard.timeline} onChange={(e) => setWizard((prev) => ({ ...prev, timeline: e.target.value }))} />
                    <div className="flex gap-2">
                      <button type="button" onClick={runGuidedSearch} className="text-xs px-3 py-1 rounded-full bg-[#C6A87C]/20 text-[#F0D8A8] border border-[#C6A87C]/40 hover:bg-[#C6A87C]/30">Run search</button>
                      <button type="button" onClick={saveGuidedSearch} className="text-xs px-3 py-1 rounded-full bg-white/5 text-[#CFC6B8] border border-white/10 hover:bg-white/10 hover:text-white">Save search</button>
                    </div>
                  </div>
                )}
              </div>

              {savedSearches.length > 0 && (
                <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-3">
                  <p className="text-xs text-[#CFC6B8] mb-2">Saved searches</p>
                  <div className="space-y-2">
                    {savedSearches.map((entry) => (
                      <div key={entry.id} className="flex items-center justify-between gap-2">
                        <button type="button" onClick={() => loadSavedSearch(entry)} className="text-left text-xs text-[#F0E9DD] hover:text-white truncate">{entry.label}</button>
                        <button type="button" onClick={() => deleteSavedSearch(entry.id)} className="text-xs text-[#B9B0A2] hover:text-white">Delete</button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-3">
                <p className="text-xs text-[#CFC6B8] mb-2">Start fast</p>
                <div className="flex flex-wrap gap-2">
                  {discoveryActions.map((item) => (
                    <button key={item.label} type="button" className="px-3 py-1 text-xs rounded-full bg-white/5 text-[#CFC6B8] hover:text-white hover:bg-white/10 border border-white/10" onClick={() => sendMessage(item.message)}>
                      {item.label}
                    </button>
                  ))}
                </div>
              </div>

              {featuredProjects.length > 0 && (
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <p className="text-xs text-[#CFC6B8]">Featured projects</p>
                    <button type="button" disabled={compareSelection.length < 2} onClick={compareSelected} className="text-xs px-2 py-1 rounded-full border border-white/10 text-[#CFC6B8] enabled:hover:text-white enabled:hover:bg-white/10 disabled:opacity-50">
                      Compare ({compareSelection.length})
                    </button>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {featuredProjects.map((project) => {
                      const selected = compareSelection.includes(project.id);
                      return (
                        <div key={project.id} className={`rounded-xl border p-3 transition-colors ${selected ? 'border-[#E9C98F]/70 bg-[#E9C98F]/10' : 'border-white/10 bg-white/[0.03]'}`}>
                          <button type="button" onClick={() => askAboutProject(project)} className="text-left w-full">
                            <p className="text-sm text-white font-medium line-clamp-1">{project.title}</p>
                            <p className="text-xs text-[#CFC6B8] line-clamp-1">{project.location || 'Location available on request'}</p>
                            <p className="text-xs text-[#E9C98F] mt-1">{project.status || 'ongoing'}</p>
                          </button>
                          <button type="button" onClick={() => toggleCompare(project.id)} className="mt-2 text-xs px-2 py-1 rounded-full border border-white/10 text-[#CFC6B8] hover:text-white hover:bg-white/10">
                            {selected ? 'Selected' : 'Select for compare'}
                          </button>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {messages.map((msg, index) => (
                <div key={`${msg.role}-${index}`} className={`p-3 text-sm whitespace-pre-wrap rounded-2xl ${msg.role === 'user' ? 'bg-gradient-to-r from-[#F5E6C6]/20 to-[#E8C890]/20 text-[#F8F3EA] ml-auto max-w-[88%] border border-[#E8C890]/20' : 'bg-white/5 text-[#F0E9DD] max-w-[92%] border border-white/10'}`}>
                  {msg.role === 'assistant' ? <div dangerouslySetInnerHTML={renderMarkdownBasic(msg.content)} /> : msg.content}
                </div>
              ))}
              {loading && <div className="text-[#CFC6B8] text-sm">Nyumba AI is thinking...</div>}
            </div>
          ) : (
            <div className="px-5 py-4 space-y-3 flex-1 min-h-0 overflow-y-auto">
              {!liveSession?.session_id ? (
                <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-4 space-y-3">
                  <p className="text-sm text-[#F0E9DD]">Request direct chat with an online admin.</p>
                  <input className="input-luxury bg-white/5 border border-white/10 text-[#F0E9DD] placeholder:text-[#B9B0A2] text-sm rounded-xl w-full" placeholder="Your name" value={liveForm.name} onChange={(e) => setLiveForm((prev) => ({ ...prev, name: e.target.value }))} />
                  <input className="input-luxury bg-white/5 border border-white/10 text-[#F0E9DD] placeholder:text-[#B9B0A2] text-sm rounded-xl w-full" placeholder="Email" value={liveForm.email} onChange={(e) => setLiveForm((prev) => ({ ...prev, email: e.target.value }))} />
                  <input className="input-luxury bg-white/5 border border-white/10 text-[#F0E9DD] placeholder:text-[#B9B0A2] text-sm rounded-xl w-full" placeholder="Phone (optional)" value={liveForm.phone} onChange={(e) => setLiveForm((prev) => ({ ...prev, phone: e.target.value }))} />
                  <textarea className="input-luxury bg-white/5 border border-white/10 text-[#F0E9DD] placeholder:text-[#B9B0A2] text-sm rounded-xl w-full min-h-[90px]" placeholder="Tell admin what you need..." value={liveForm.initial_message} onChange={(e) => setLiveForm((prev) => ({ ...prev, initial_message: e.target.value }))} />
                  <button type="button" onClick={startLiveChat} disabled={liveLoading} className="text-sm px-4 py-2 rounded-full bg-[#C6A87C]/20 text-[#F0D8A8] border border-[#C6A87C]/40 hover:bg-[#C6A87C]/30 disabled:opacity-60">
                    {liveLoading ? 'Connecting...' : 'Connect to live admin'}
                  </button>
                </div>
              ) : (
                <>
                  <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-3">
                    <p className="text-xs text-[#CFC6B8]">Session status</p>
                    <p className="text-sm text-white">
                      {liveSession.status === 'waiting' ? 'Waiting for admin to accept...' : liveSession.status === 'active' ? 'Connected to admin' : 'Session closed'}
                    </p>
                  </div>
                  <div className="space-y-2">
                    {liveMessages.map((msg, idx) => (
                      <div
                        key={`${msg.id || idx}`}
                        className={`p-3 text-sm rounded-2xl ${
                          msg.sender_type === 'guest'
                            ? 'bg-gradient-to-r from-[#F5E6C6]/20 to-[#E8C890]/20 text-[#F8F3EA] ml-auto max-w-[88%] border border-[#E8C890]/20'
                            : msg.sender_type === 'admin'
                            ? 'bg-white/5 text-[#F0E9DD] max-w-[92%] border border-white/10'
                            : 'bg-red-500/10 text-red-200 border border-red-400/20'
                        }`}
                      >
                        {msg.content}
                      </div>
                    ))}
                    {!liveMessages.length && <p className="text-xs text-[#A3A3A3]">No messages yet.</p>}
                  </div>
                  <div className="flex items-center gap-2">
                    <input
                      type="text"
                      value={liveInput}
                      onChange={(e) => setLiveInput(e.target.value)}
                      placeholder={liveSession.status === 'active' ? 'Type your message...' : 'You can type while waiting...'}
                      className="input-luxury flex-1 rounded-full bg-white/5 border border-white/10 text-[#F0E9DD] placeholder:text-[#B9B0A2]"
                      disabled={liveLoading || liveSession.status === 'closed'}
                    />
                    <button type="button" onClick={sendLiveMessage} disabled={liveLoading || liveSession.status === 'closed'} className="btn-primary rounded-full px-4 bg-gradient-to-r from-[#F0D8A8] to-[#E2B774] text-[#1A1208] disabled:opacity-50">
                      Send
                    </button>
                  </div>
                </>
              )}
            </div>
          )}

          <div className="px-5 pb-4 pt-2 border-t border-white/10 shrink-0 bg-[#0A0908]/85">
            {activeTab === 'ai' && (
              <>
                <div className="flex flex-wrap gap-2 mb-3">
                  <Link to="/projects" className="px-3 py-1 text-xs rounded-full bg-white/5 text-[#CFC6B8] hover:text-white hover:bg-white/10 border border-white/10">Browse all projects</Link>
                  <Link to="/consultation" className="px-3 py-1 text-xs rounded-full bg-[#C6A87C]/20 text-[#F0D8A8] hover:bg-[#C6A87C]/30 border border-[#C6A87C]/40">Talk to a person</Link>
                </div>
                <form onSubmit={(e) => { e.preventDefault(); sendMessage(input); }} className="flex items-center gap-3">
                  <input type="text" value={input} onChange={(e) => setInput(e.target.value)} placeholder="Example: 3-bedroom home in Kampala under UGX 900M..." className="input-luxury flex-1 rounded-full bg-white/5 border border-white/10 text-[#F0E9DD] placeholder:text-[#B9B0A2]" disabled={loading} />
                  <button type="submit" className="btn-primary rounded-full px-5 bg-gradient-to-r from-[#F0D8A8] to-[#E2B774] text-[#1A1208]" disabled={loading}>Send</button>
                </form>
              </>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default FloatingAbkasanAI;
